import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { FileText, Plus, Edit, Trash2, Save, Info } from "lucide-react";

const PLACEHOLDERS = [
  "nome_studio", "p_iva", "codice_fiscale", "indirizzo", "citta", "cap", "provincia",
  "telefono", "email", "pec", "iban", "legale_rappresentante",
  "piano_scelto", "n_condomini", "sezioni_attive", "servizi_extra",
  "corrispettivo_netto", "corrispettivo_iva", "corrispettivo_totale",
  "modalita_pagamento", "scadenza_giorni", "data_decorrenza",
  "prorata_giorni", "prorata_importo", "dpo_riferimento",
  "luogo_firma", "data_firma", "ginius_sede", "ginius_piva",
];

/** Iter122bm · SuperAdmin · Modelli di contratto editabili. */
export default function SuperAdminContrattiModelli() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/superadmin/contratti-modelli").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.titolo.trim()) { toast.error("Titolo obbligatorio"); return; }
    setBusy(true);
    try {
      if (edit.id) {
        await api.put(`/superadmin/contratti-modelli/${edit.id}`, { titolo: edit.titolo, corpo: edit.corpo, note: edit.note });
        toast.success("Modello aggiornato");
      } else {
        await api.post(`/superadmin/contratti-modelli`, { titolo: edit.titolo, corpo: edit.corpo, note: edit.note });
        toast.success("Modello creato");
      }
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setBusy(false);
  };

  const del = async (m) => {
    if (!window.confirm(`Eliminare il modello "${m.titolo}"?`)) return;
    try { await api.delete(`/superadmin/contratti-modelli/${m.id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-contratti-modelli">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-brand" />
          <h3 className="font-display text-lg font-bold text-slate-800">Modelli di contratto</h3>
        </div>
        <Button size="sm" onClick={() => setEdit({ titolo: "", corpo: "", note: "" })} data-testid="contratto-modello-nuovo" className="h-9 bg-brand hover:bg-brand-dark">
          <Plus className="w-4 h-4 mr-1" /> Nuovo modello
        </Button>
      </div>

      <Card className="p-3 border-2 border-blue-200 bg-blue-50">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900">
            <p className="font-semibold mb-1">Placeholder disponibili nel corpo del modello:</p>
            <p className="font-mono text-[10px] leading-relaxed">{PLACEHOLDERS.map((p) => `{{${p}}}`).join(" · ")}</p>
            <p className="mt-1">I placeholder vengono sostituiti in automatico alla generazione del PDF con i dati anagrafici del tenant + snapshot del fornitore anagrafica (se collegato) + parametri contrattuali (piano, corrispettivo, ecc.).</p>
          </div>
        </div>
      </Card>

      {items.map((m) => (
        <Card key={m.id} className="p-4 border-2" data-testid={`contratto-modello-${m.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800">{m.titolo}</span>
                {m.is_default && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Default</span>}
              </div>
              {m.note && <p className="text-xs text-slate-500 mt-1">{m.note}</p>}
              <p className="text-xs text-slate-400 mt-1">Aggiornato il {new Date(m.updated_at || m.created_at).toLocaleDateString("it-IT")}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEdit({ ...m })} data-testid={`contratto-modello-edit-${m.id}`} className="h-8">
                <Edit className="w-3.5 h-3.5 mr-1" /> Modifica
              </Button>
              {!m.is_default && (
                <Button size="sm" variant="outline" onClick={() => del(m)} data-testid={`contratto-modello-del-${m.id}`} className="h-8 text-red-600 border-red-300 hover:bg-red-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-display text-xl">{edit?.id ? "Modifica modello contratto" : "Nuovo modello contratto"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div>
                <Label>Titolo *</Label>
                <Input value={edit.titolo} onChange={(e) => setEdit({ ...edit, titolo: e.target.value })} className="h-11 mt-1" data-testid="contratto-titolo" placeholder="Es. Contratto standard di servizio Ginius" />
              </div>
              <div>
                <Label>Note interne (non stampate)</Label>
                <Input value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} className="h-10 mt-1" data-testid="contratto-note" />
              </div>
              <div>
                <Label>Corpo del contratto (usa i placeholder {"{{...}}"} sopra)</Label>
                <textarea value={edit.corpo || ""} onChange={(e) => setEdit({ ...edit, corpo: e.target.value })}
                          data-testid="contratto-corpo"
                          className="w-full mt-1 border-2 border-slate-200 rounded-md p-3 font-mono text-xs min-h-[400px] focus:outline-brand" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={save} disabled={busy} data-testid="contratto-save" className="h-10 bg-brand hover:bg-brand-dark">
                  <Save className="w-4 h-4 mr-1" /> {busy ? "Salvataggio…" : "Salva modello"}
                </Button>
                <Button variant="outline" onClick={() => setEdit(null)} className="h-10">Annulla</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
